/**
 * PESTANA "MEDICOS Y CITAS" DE LA FICHA.
 *
 * Punto 10 de la lista de Lissette (2026-09-15), fase 1. El 2026-09-16, con las
 * capturas de Apizeal delante: ***"si por persona, que lo vean todos, arranca la
 * fase 1"***.
 *
 * ── LAS DOS DECISIONES QUE SE VEN EN LA PANTALLA ────────────────────────────
 *
 * "POR PERSONA": la lista va agrupada por quien de la casa es —titular,
 * conyuge, cada dependiente— y cada grupo tiene su propio boton de agregar. En
 * Apizeal es un solo cuadro de texto para toda la ficha, y en una familia de
 * cuatro con cuatro PCP distintos eso obliga a escribirlos revueltos.
 *
 * "QUE LO VEAN TODOS": no hay PIN ni pantalla de bloqueo, al contrario que los
 * metodos de pago. Quien puede abrir la ficha ve los medicos. Lo sensible se
 * protege igual —el detalle esta cifrado en la base y no entra en ningun log—
 * pero no se le esconde a la agencia.
 *
 * ── LO QUE SE COPIA DE APIZEAL Y LO QUE NO ──────────────────────────────────
 *
 * Se copia la FIRMA (en la captura la escriben a mano: "09/15/2026 JA:") y el
 * DE DONDE SALIO EL DATO ("tomada de la orden de referido del 02/10/2026"). La
 * firma la pone el sistema, asi que no se puede olvidar ni falsear.
 *
 * No se copia el cuadro de texto libre: van campos, que es lo que permite
 * buscar un medico, sacarlo en un reporte y meter SOLO el medico en el
 * recordatorio de una cita de la fase 3.
 *
 * ── LAS CITAS (fase 2) VAN DEBAJO, EN SU BLOQUE ─────────────────────────────
 *
 * En Apizeal son dos sitios distintos —el cuadro del medico vive en el resumen
 * del contacto y las citas cuelgan de "Tareas y Recordatorios"— y hay que ir y
 * volver. Aqui van en la misma pestaña porque se consultan juntas: quien abre
 * esto pregunta "¿quien lo atiende y cuando le toca?". La tarjeta y el
 * formulario viven en `CitasMedicas.jsx`, que comparte con la lista global.
 *
 * ── UN MEDICO NUNCA SE ESCONDE ──────────────────────────────────────────────
 *
 * Si la persona de la que colgaba ya no esta en la ficha —se quito el
 * dependiente, o se paso a ficha propia— su medico sale igual, en un grupo
 * aparte y con el aviso, para poder moverlo o borrarlo. Esconderlo porque su
 * dueño ya no aparece es la forma de perder un dato sin que nadie se entere.
 */
import { useState, useEffect, useMemo } from 'react';
import { Modal, Input, Textarea, Select, confirmDialog } from './ui.jsx';
import { toast } from './Toast.jsx';
import { fmt } from '../utils.js';
import { personasDelHogar, nombreDePersona } from '../personasDelHogar.js';
import {
  CAMPOS_DEL_MEDICO, LARGO_MAXIMO, ETIQUETAS,
  medicoVacio, medicoComoFormulario, resumenDelMedico, llenarConProveedorDeLaAgencia,
} from '../medicosDelCliente.js';
import {
  listClientDoctors, createClientDoctor, updateClientDoctor, deleteClientDoctor,
} from '../api.js';
import {
  TarjetaDeCita, FormularioDeCita, useCitasMedicas,
  citaVacia, citaComoFormulario,
} from './CitasMedicas.jsx';
import MedicamentosDelCliente from './MedicamentosDelCliente.jsx';
import BuscadorDeMedico from './BuscadorDeMedico.jsx';
import MisProveedores from './MisProveedores.jsx';
import { AddressSuggestions } from './AddressSuggestions.jsx';
import { llenarConMedicoDelDirectorio, direccionDelMedico } from '../medicosDelMercado.js';

export default function MedicosTab({ client, lang = 'es' }) {
  const es = lang !== 'en';
  const clientId = client?.id;
  const [medicos, setMedicos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [errorDeCarga, setErrorDeCarga] = useState('');
  const [form, setForm] = useState(null);      // el formulario abierto, o null
  const [editando, setEditando] = useState(null); // el medico que se edita, o null
  const [guardando, setGuardando] = useState(false);
  // Las pestañas de la ventana (Lissette 2026-11-01, E-405): 'mios' = los
  // proveedores que la agencia ya registró en otras fichas, 'mercado' = el
  // directorio del Mercado (2026-10-15: "Dr. especialista, así como está en el
  // Mercado"), 'nuevo' = escribirlo a mano (y donde se revisa y se guarda).
  const [pestana, setPestana] = useState('nuevo');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // El corrector de direcciones de la ficha del cliente, también aquí (Lissette
  // 2026-10-23). Solo pregunta cuando se TECLEA en la dirección, nunca al abrir.
  const [dirTecleada, setDirTecleada] = useState(false);
  // Las casillas de la dirección que, al teclear, piden sugerencias.
  const DISPARAN_SUGERENCIAS = new Set(['direccion', 'ciudad', 'zip']);

  const personas = useMemo(() => personasDelHogar(client, lang), [client, lang]);

  const cargar = async () => {
    if (!clientId) return;
    setCargando(true); setErrorDeCarga('');
    try { setMedicos(await listClientDoctors(clientId)); }
    catch (e) { setErrorDeCarga(e.message || (es ? 'No se pudo cargar' : 'Could not load')); }
    finally { setCargando(false); }
  };
  useEffect(() => { cargar(); }, [clientId]);

  /**
   * Los grupos que se dibujan: una seccion por persona de la casa, y detras las
   * de las claves huerfanas (ver la cabecera). Las personas sin medico SALEN
   * IGUAL: es asi como se ve de un golpe a quien le falta el PCP, que es la
   * pregunta que de verdad se hace la agencia.
   */
  const grupos = useMemo(() => {
    const porPersona = new Map();
    for (const m of medicos) {
      const k = m.persona || 'primary';
      if (!porPersona.has(k)) porPersona.set(k, []);
      porPersona.get(k).push(m);
    }
    const salida = personas.map((p) => ({ ...p, medicos: porPersona.get(p.key) || [] }));
    const conocidas = new Set(personas.map((p) => p.key));
    for (const [k, lista] of porPersona) {
      if (conocidas.has(k)) continue;
      salida.push({
        key: k,
        nombre: nombreDePersona(client, k, lang),
        parentesco: es ? 'Se quitó del hogar' : 'Removed from household',
        huerfana: true,
        medicos: lista,
      });
    }
    return salida;
  }, [medicos, personas, client, lang, es]);

  const abrirNuevo = (persona) => { setEditando(null); setForm(medicoVacio(persona)); setPestana('mios'); setDirTecleada(false); };
  const abrirEdicion = (m) => { setEditando(m); setForm(medicoComoFormulario(m)); setPestana('nuevo'); setDirTecleada(false); };
  const cerrar = () => { setForm(null); setEditando(null); };

  const guardar = async () => {
    if (!form || guardando) return;
    if (!String(form.nombre || '').trim()) {
      toast.error(es ? 'Escribe el nombre del médico' : 'Enter the doctor name');
      return;
    }
    setGuardando(true);
    try {
      const payload = { persona: form.persona };
      for (const c of CAMPOS_DEL_MEDICO) payload[c] = form[c] || '';
      const r = editando
        ? await updateClientDoctor(clientId, editando.id, payload)
        : await createClientDoctor(clientId, payload);
      await cargar();
      cerrar();
      // Si el servidor corto algo por largo, se dice: un recorte callado es una
      // mentira, y la agente tiene que poder volver a entrar y acortarlo ella.
      if (r?.recortados?.length) {
        const nombres = r.recortados.map((c) => (es ? ETIQUETAS[c]?.es : ETIQUETAS[c]?.en) || c).join(', ');
        toast.error(es
          ? `Guardado, pero se acortó por ser muy largo: ${nombres}`
          : `Saved, but trimmed for length: ${nombres}`);
      } else {
        toast.success(es ? 'Médico guardado' : 'Doctor saved');
      }
    } catch (e) {
      toast.error(e.message || (es ? 'No se pudo guardar' : 'Save failed'));
    } finally {
      setGuardando(false);
    }
  };

  const borrar = async (m) => {
    const ok = await confirmDialog({
      title: es ? '¿Quitar este médico?' : 'Remove this doctor?',
      message: (m.nombre || '') + (es
        ? ' — se quita de la ficha. Las citas que ya se hayan escrito no se tocan.'
        : ' — removed from the record. Appointments already written are untouched.'),
      confirmLabel: es ? 'Quitar' : 'Remove',
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteClientDoctor(clientId, m.id);
      await cargar();
      toast.success(es ? 'Médico quitado' : 'Doctor removed');
    } catch (e) {
      toast.error(e.message || (es ? 'No se pudo quitar' : 'Delete failed'));
    }
  };

  const etiqueta = (c) => (es ? ETIQUETAS[c]?.es : ETIQUETAS[c]?.en) || c;
  const pista = (c) => (es ? ETIQUETAS[c]?.pistaEs : ETIQUETAS[c]?.pistaEn) || '';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.75rem', marginBottom: '.85rem', flexWrap: 'wrap' }}>
        <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', lineHeight: 1.5, maxWidth: 520 }}>
          {es
            ? 'El médico de cada persona de la casa: quién es, de qué red y de dónde salió el dato. Lo ve todo el que puede abrir la ficha.'
            : 'Each household member’s doctor: who they are, which network, and where the data came from. Visible to anyone who can open the record.'}
        </div>
        {!cargando && !!medicos.length && (
          <span style={{ fontSize: '.65rem', padding: '.2rem .6rem', borderRadius: 100, background: 'rgba(0,212,255,.08)', border: '1px solid rgba(0,212,255,.2)', color: 'var(--c-cyan,#00d4ff)', fontWeight: 700 }}>
            🩺 {medicos.length} {es ? (medicos.length === 1 ? 'médico' : 'médicos') : (medicos.length === 1 ? 'doctor' : 'doctors')}
          </span>
        )}
      </div>

      {cargando && (
        <div style={{ textAlign: 'center', padding: '1.5rem 1rem', color: 'var(--c-text3,#45688a)', fontSize: '.8rem' }}>
          {es ? 'Cargando…' : 'Loading…'}
        </div>
      )}

      {!!errorDeCarga && !cargando && (
        <div style={{ padding: '.75rem 1rem', marginBottom: '.6rem', borderRadius: 10, background: 'rgba(255,61,107,.08)', border: '1px solid rgba(255,61,107,.25)', color: 'var(--c-red,#ff3d6b)', fontSize: '.78rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.75rem' }}>
          <span>{errorDeCarga}</span>
          <button type="button" onClick={cargar} className="dkt-btn-s" style={{ fontSize: '.72rem', padding: '.3rem .75rem' }}>
            {es ? 'Reintentar' : 'Retry'}
          </button>
        </div>
      )}

      {!cargando && !errorDeCarga && !personas.length && (
        <div style={{ textAlign: 'center', padding: '2rem 1rem', color: 'var(--c-text3,#45688a)', background: 'var(--c-surface2,#112538)', borderRadius: 12, border: '1px dashed var(--c-border,#1a3550)', fontSize: '.8rem' }}>
          {es ? 'Esta ficha todavía no tiene nombre puesto.' : 'This record has no name yet.'}
        </div>
      )}

      {!cargando && !errorDeCarga && grupos.map((g) => (
        <div key={g.key} style={{ marginBottom: '.9rem', background: 'var(--c-surface2,#112538)', border: `1px solid ${g.huerfana ? 'rgba(255,176,32,.3)' : 'var(--c-border,#1a3550)'}`, borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ padding: '.6rem .85rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.6rem', borderBottom: '1px solid rgba(26,53,80,.6)', flexWrap: 'wrap' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: '.85rem', color: g.huerfana ? 'var(--c-amber,#ffb700)' : 'var(--c-white,#eef6ff)' }}>
                {g.huerfana ? '⚠️ ' : ''}{g.nombre}
              </div>
              <div style={{ fontSize: '.65rem', color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.06em', fontFamily: 'var(--font-mono)' }}>
                {g.parentesco}
              </div>
            </div>
            {!g.huerfana && (
              <button type="button" onClick={() => abrirNuevo(g.key)} className="dkt-btn-s" style={{ fontSize: '.72rem', padding: '.3rem .7rem' }}>
                + {es ? 'Médico' : 'Doctor'}
              </button>
            )}
          </div>

          {!g.medicos.length && (
            <div style={{ padding: '.8rem .85rem', fontSize: '.75rem', color: 'var(--c-text3,#45688a)' }}>
              {es ? 'Sin médico puesto todavía.' : 'No doctor on file yet.'}
            </div>
          )}

          {g.medicos.map((m) => (
            <div key={m.id} style={{ padding: '.7rem .85rem', borderTop: '1px solid rgba(26,53,80,.45)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '.6rem', flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: '.85rem', color: 'var(--c-white,#eef6ff)' }}>
                    🩺 {m.nombre || (es ? '(sin nombre)' : '(no name)')}
                  </div>
                  {!!resumenDelMedico(m, lang) && (
                    <div style={{ fontSize: '.72rem', color: 'var(--c-text2,#7aa3c0)', marginTop: 2 }}>
                      {resumenDelMedico(m, lang)}
                    </div>
                  )}
                  {(m.telefono || m.fax) && (
                    <div style={{ fontSize: '.72rem', color: 'var(--c-text2,#7aa3c0)', marginTop: 2, fontFamily: 'var(--font-mono)' }}>
                      {m.telefono ? `☎ ${m.telefono}` : ''}{m.telefono && m.fax ? '  ·  ' : ''}{m.fax ? `🖨 ${m.fax}` : ''}
                    </div>
                  )}
                  {!!direccionDelMedico(m) && (
                    <div style={{ fontSize: '.72rem', color: 'var(--c-text2,#7aa3c0)', marginTop: 2 }}>📍 {direccionDelMedico(m)}</div>
                  )}
                  {!!m.notas && (
                    <div style={{ fontSize: '.75rem', color: 'var(--c-text,#c8dff0)', marginTop: '.35rem', whiteSpace: 'pre-wrap' }}>{m.notas}</div>
                  )}
                  {!!m.origen && (
                    <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', marginTop: '.35rem', fontStyle: 'italic' }}>
                      {es ? 'De dónde salió: ' : 'Source: '}{m.origen}
                    </div>
                  )}
                  {/* LA FIRMA. Lo que en Apizeal se escribe a mano ("09/15/2026 JA:")
                      y aqui lo pone el sistema. Se enseña quien lo escribio, y solo
                      se añade la linea de "actualizado" cuando de verdad cambio. */}
                  <div style={{ fontSize: '.65rem', color: 'var(--c-text3,#45688a)', marginTop: '.45rem', fontFamily: 'var(--font-mono)' }}>
                    ✍️ {es ? 'Escrito por' : 'Written by'} {m.creadoPor || (es ? 'alguien de la agencia' : 'someone at the agency')} · {fmt(m.createdAt)}
                    {m.updatedAt && new Date(m.updatedAt) - new Date(m.createdAt) > 60000 && (
                      <> · {es ? 'actualizado por' : 'updated by'} {m.actualizadoPor || '—'} · {fmt(m.updatedAt)}</>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '.35rem', flexShrink: 0 }}>
                  <button type="button" onClick={() => abrirEdicion(m)} className="dkt-btn-s" style={{ fontSize: '.68rem', padding: '.25rem .6rem' }}>
                    {es ? 'Editar' : 'Edit'}
                  </button>
                  <button type="button" onClick={() => borrar(m)}
                    style={{ background: 'rgba(255,61,107,.08)', border: '1px solid rgba(255,61,107,.25)', borderRadius: 7, padding: '.25rem .6rem', cursor: 'pointer', fontSize: '.68rem', fontWeight: 700, color: 'var(--c-red,#ff3d6b)' }}>
                    {es ? 'Quitar' : 'Remove'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ))}

      {/* LOS MEDICAMENTOS (Lissette 2026-10-15), entre los médicos y las citas:
          quien los receta suele ser uno de los médicos de arriba. */}
      <MedicamentosDelCliente client={client} personas={personas} medicos={medicos} lang={lang} />

      <BloqueDeCitas client={client} personas={personas} medicos={medicos} lang={lang} />

      {!!form && (
        <Modal
          title={`🩺 ${editando ? (es ? 'Editar médico' : 'Edit doctor') : (es ? 'Nuevo médico' : 'New doctor')}`}
          onClose={cerrar}
          wide
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.6rem' }}>
            {/* De quien es. Se puede cambiar tambien al editar: un medico apuntado
                al titular por error se mueve al hijo sin tener que reescribirlo. */}
            <div style={{ gridColumn: 'span 2' }}>
              <Select label={es ? '¿De quién es este médico?' : 'Whose doctor is this?'}
                value={form.persona} onChange={(e) => set('persona', e.target.value)}>
                {personas.map((p) => (
                  <option key={p.key} value={p.key}>{p.nombre} — {p.parentesco}</option>
                ))}
                {/* La clave huerfana se queda como opcion mientras se edita uno de
                    los suyos, o el desplegable no podria enseñar donde esta. */}
                {!personas.some((p) => p.key === form.persona) && (
                  <option value={form.persona}>{nombreDePersona(client, form.persona, lang)}</option>
                )}
              </Select>
            </div>
            {/* LAS PESTAÑAS (E-405): elegir en "Mis proveedores" o en el
                directorio del Mercado llena los datos y pasa a la pestaña del
                formulario, para revisarlo y guardar. */}
            <div style={{ gridColumn: 'span 2', display: 'flex', gap: '.35rem', flexWrap: 'wrap' }}>
              {[
                ['mios', es ? '📋 Mis proveedores' : '📋 My providers'],
                ['mercado', es ? '🔎 Directorio del Mercado' : '🔎 Marketplace directory'],
                ['nuevo', editando ? (es ? '✍️ Datos del médico' : '✍️ Doctor details') : (es ? '✍️ Nuevo' : '✍️ New')],
              ].map(([id, texto]) => (
                <button key={id} type="button" onClick={() => setPestana(id)} className={`dkt-tab${pestana === id ? ' on' : ''}`}>{texto}</button>
              ))}
            </div>
            {pestana === 'mios' && (
              <div style={{ gridColumn: 'span 2' }}>
                <MisProveedores lang={lang}
                  onElegir={(p) => {
                    setForm((f) => llenarConProveedorDeLaAgencia(f, p));
                    setPestana('nuevo');
                    toast.success(es ? 'Datos del proveedor puestos. Revisa y guarda.' : 'Provider details filled in. Review and save.');
                  }} />
              </div>
            )}
            {pestana === 'mercado' && (
              <div style={{ gridColumn: 'span 2' }}>
                <BuscadorDeMedico zipInicial={client?.zipCode || client?.addressZip || ''} lang={lang}
                  onElegir={(m) => {
                    setForm((f) => llenarConMedicoDelDirectorio(f, m));
                    setPestana('nuevo');
                    toast.success(es ? 'Datos del médico puestos. Revisa y guarda.' : 'Doctor details filled in. Review and save.');
                  }} />
              </div>
            )}
            {pestana === 'nuevo' && CAMPOS_DEL_MEDICO.map((c) => {
              const meta = ETIQUETAS[c] || {};
              const estilo = meta.ancho === 2 ? { gridColumn: 'span 2' } : undefined;
              return (
                <div key={c} style={estilo}>
                  {meta.area
                    ? <Textarea label={etiqueta(c)} maxLength={LARGO_MAXIMO[c]}
                        value={form[c]} placeholder={pista(c)}
                        onChange={(e) => set(c, e.target.value)} />
                    : <Input label={etiqueta(c) + (c === 'nombre' ? ' *' : '')} maxLength={LARGO_MAXIMO[c]}
                        value={form[c]} placeholder={pista(c)}
                        onChange={(e) => { if (DISPARAN_SUGERENCIAS.has(c)) setDirTecleada(true); set(c, e.target.value); }} />}
                  {/* Calle, ciudad, estado y ZIP van aparte: al elegir una
                      sugerencia se llena cada casilla, igual que en la cita. */}
                  {c === 'direccion' && (
                    <AddressSuggestions
                      query={{ street: form.direccion || '', city: form.ciudad || '', state: form.estado || '', zip: form.zip || '' }}
                      enabled={dirTecleada} lang={lang}
                      onPick={(s) => {
                        setDirTecleada(false);
                        setForm((f) => ({
                          ...f, direccion: s.street || f.direccion, ciudad: s.city || f.ciudad,
                          estado: s.state || f.estado, zip: s.zip || f.zip,
                        }));
                      }} />
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: '.5rem', marginTop: '.9rem' }}>
            {pestana === 'nuevo' && <button type="button" onClick={guardar} disabled={guardando} className="dkt-btn-p" style={{ flex: 1, opacity: guardando ? 0.6 : 1 }}>
              {guardando ? (es ? 'Guardando…' : 'Saving…') : (es ? 'Guardar' : 'Save')}
            </button>}
            <button type="button" onClick={cerrar} className="dkt-btn-s" style={{ flex: 1 }}>
              {es ? 'Cancelar' : 'Cancel'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/**
 * LAS CITAS MEDICAS DE ESTE CLIENTE (fase 2).
 *
 * Va debajo de los medicos y no en una pestaña aparte porque se consultan
 * juntos: quien abre esto pregunta "¿quien lo atiende y cuando le toca?".
 *
 * Aqui NO se agrupa por persona como arriba: una lista de citas se lee POR
 * FECHA —la proxima primero— y partirla en cuatro grupos esconderia la que es
 * mañana debajo de la de un hijo que es en marzo. De quien es se dice en cada
 * tarjeta.
 */
function BloqueDeCitas({ client, personas, medicos, lang }) {
  const es = lang !== 'en';
  const clientId = client?.id;
  const { citas, cargando, errorDeCarga, hayMas, guardando, cambiando, cargar, guardar, borrar, cambiarEstatus } =
    useCitasMedicas({ clientId, lang });
  const [form, setForm] = useState(null);
  const [editando, setEditando] = useState(null);

  const cerrar = () => { setForm(null); setEditando(null); };
  const abrirNueva = () => {
    setEditando(null);
    setForm(citaVacia(personas[0]?.key || 'primary'));
  };
  const abrirEdicion = (cita) => { setEditando(cita); setForm(citaComoFormulario(cita)); };

  const nombreDe = (key) => personas.find((p) => p.key === key)?.nombre || '';

  // Las que ya pasaron se guardan al final: la lista se abre por lo que viene.
  const { proximas, pasadas } = useMemo(() => {
    const ahora = Date.now();
    const p = [], q = [];
    for (const c of citas) (new Date(c.startsAt).getTime() >= ahora ? p : q).push(c);
    q.reverse(); // la más reciente primero
    return { proximas: p, pasadas: q };
  }, [citas]);
  const [verPasadas, setVerPasadas] = useState(false);

  return (
    <div style={{ marginTop: '1.4rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.75rem', marginBottom: '.6rem', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: '.92rem', color: 'var(--c-white,#eef6ff)' }}>
            📅 {es ? 'Citas médicas' : 'Medical appointments'}
          </div>
          <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)' }}>
            {es
              ? 'Ordenadas por fecha, la próxima arriba. También salen todas juntas en Tareas → Citas médicas.'
              : 'Sorted by date, the next one first. They also show up together in Tasks → Medical appointments.'}
          </div>
        </div>
        <button type="button" onClick={abrirNueva} className="dkt-btn-p" style={{ fontSize: '.76rem' }}>
          + {es ? 'Nueva cita' : 'New appointment'}
        </button>
      </div>

      {cargando && (
        <div style={{ textAlign: 'center', padding: '1.2rem 1rem', color: 'var(--c-text3,#45688a)', fontSize: '.8rem' }}>
          {es ? 'Cargando…' : 'Loading…'}
        </div>
      )}

      {!!errorDeCarga && !cargando && (
        <div style={{ padding: '.75rem 1rem', borderRadius: 10, background: 'rgba(255,61,107,.08)',
          border: '1px solid rgba(255,61,107,.25)', color: 'var(--c-red,#ff3d6b)', fontSize: '.78rem',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.75rem' }}>
          <span>{errorDeCarga}</span>
          <button type="button" onClick={cargar} className="dkt-btn-s" style={{ fontSize: '.72rem', padding: '.3rem .75rem' }}>
            {es ? 'Reintentar' : 'Retry'}
          </button>
        </div>
      )}

      {!cargando && !errorDeCarga && !citas.length && (
        <div style={{ textAlign: 'center', padding: '1.8rem 1rem', color: 'var(--c-text3,#45688a)',
          background: 'var(--c-surface2,#112538)', borderRadius: 12, border: '1px dashed var(--c-border,#1a3550)' }}>
          <div style={{ fontSize: '1.6rem', marginBottom: '.3rem' }}>📅</div>
          <div style={{ fontSize: '.8rem', color: 'var(--c-text,#c8dff0)' }}>
            {es ? 'Sin citas médicas apuntadas' : 'No medical appointments on file'}
          </div>
        </div>
      )}

      {!cargando && !errorDeCarga && !!citas.length && (
        <div style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, overflow: 'hidden' }}>
          {proximas.map((c) => (
            <TarjetaDeCita key={c.id} cita={c} lang={lang} nombreDePersona={nombreDe(c.persona)}
              onEditar={abrirEdicion} onBorrar={borrar} cambiando={cambiando[c.id]}
              onEstatus={(s) => cambiarEstatus(c, s)} />
          ))}
          {!proximas.length && (
            <div style={{ padding: '.8rem .85rem', fontSize: '.75rem', color: 'var(--c-text3,#45688a)' }}>
              {es ? 'No hay ninguna cita por venir.' : 'No upcoming appointments.'}
            </div>
          )}
          {/* Las pasadas NO se esconden: se pliegan, y el botón dice cuántas
              son. Un "ver más" que no cuenta parece que no hay nada. */}
          {!!pasadas.length && (
            <button type="button" onClick={() => setVerPasadas((v) => !v)}
              style={{ width: '100%', background: 'var(--c-surface3,#162d44)', border: 'none',
                borderTop: '1px solid rgba(26,53,80,.6)', padding: '.5rem', cursor: 'pointer',
                fontSize: '.72rem', fontWeight: 700, color: 'var(--c-text2,#7aa3c0)', fontFamily: 'inherit' }}>
              {verPasadas ? '▾ ' : '▸ '}
              {es ? `${pasadas.length} cita${pasadas.length === 1 ? '' : 's'} ya pasada${pasadas.length === 1 ? '' : 's'}`
                  : `${pasadas.length} past appointment${pasadas.length === 1 ? '' : 's'}`}
            </button>
          )}
          {verPasadas && pasadas.map((c) => (
            <TarjetaDeCita key={c.id} cita={c} lang={lang} nombreDePersona={nombreDe(c.persona)}
              onEditar={abrirEdicion} onBorrar={borrar} cambiando={cambiando[c.id]}
              onEstatus={(s) => cambiarEstatus(c, s)} />
          ))}
        </div>
      )}

      {hayMas && (
        <div style={{ fontSize: '.68rem', color: 'var(--c-amber,#ffb700)', marginTop: '.4rem' }}>
          {es ? 'Hay más citas de las que caben aquí; la lista de Tareas → Citas médicas las filtra.'
              : 'There are more appointments than fit here; the Tasks → Medical appointments list can filter them.'}
        </div>
      )}

      {!!form && (
        <FormularioDeCita
          titulo={`📅 ${editando ? (es ? 'Editar cita' : 'Edit appointment') : (es ? 'Nueva cita médica' : 'New medical appointment')}`}
          valor={form} personas={personas} medicos={medicos} lang={lang} guardando={guardando}
          onCambiar={setForm} onCerrar={cerrar}
          onGuardar={async () => { if (await guardar(form, editando, clientId)) cerrar(); }} />
      )}
    </div>
  );
}
